import React from 'react';
import { Box, Typography, Button, Slider, TextField } from '@mui/material';
import { SCRUB_STEP } from '../../utils/playbookTimeline';
import {
  FORMATION_FULL_SCRUB, SNAP_FULL_SCRUB, END_FULL_SCRUB,
  isAtFormation, isAtEnd,
} from '../../utils/playbookLifecycle';

// Playbook Phase B.2.1 — PTP scrubber/playback UX pass. Extracted out of
// TeamPlaybookPlayEditor.js (§27 — the PTP control surface had grown
// large enough, and a future read-only Athlete Study surface will want
// this exact same control strip over its own read-only position state)
// into its own small, self-contained, PURELY presentational component:
// every mutation goes back out through the callback props the caller
// supplies.
//
// Phase B.2.3 unified-editor pass: Play/Pause is no longer owned here.
// usePlaybackAnimation() lives in the CALLER (DualMarkerField in
// TeamPlaybookPlayEditor.js) instead, purely so the caller can pause
// playback from OTHER gestures this component has no visibility into.
// This component still owns "manual interaction pauses playback first."
//
// Phase B.3 — FULL LIFECYCLE. Scrub value is `fullScrub` (0=Formation..
// 1=End); this component stays a purely presentational consumer of
// playbookLifecycle.js's fixed anchor constants/helpers — no knowledge
// of `timeline.keyframes`, assignments, or phase-local math (the caller
// pre-resolves `moments`/`selectedKeyframe`).
//
// ─────────────────────────────────────────────────────────────────────
// Phase B.3b — COMPACT PTP PLAYER. This is a near-total layout rewrite
// (§15-§30 of the B.3b spec: "the bottom lifecycle/PTP HUD currently
// occupies too much vertical field space"). What changed and why:
//
//   - Formation/Snap/End were three separate full-size Button elements
//     plus two nudge-arrow buttons plus a slider, all on their own row,
//     PLUS a phase/percent text row, PLUS a permanent Moment chip row,
//     PLUS a conditional Rename/Delete row — five rows in the resting
//     state. Now: ONE main row (Play/Pause + Mark Moment + Formation
//     label + slider + End label) plus one THIN landmark-label row
//     (SNAP text + tappable Moment dots) living directly under the
//     slider, plus a CONTEXTUAL row for Moment name/Rename/Delete. Three
//     short rows total — see this row's own note below for why it is
//     NOW always rendered (Phase B.3h.2) rather than conditionally
//     mounted, keeping this component's total height CONSTANT
//     regardless of whether a Moment is currently selected (a coach
//     scrubbing/playing through a Moment used to visibly twitch the
//     whole HUD/field area taller and shorter every time; that's gone).
//   - Nudge (◀/▶) buttons REMOVED entirely (§23) — continuous scrubbing
//     already gives the coach fine-grained control at the same
//     SCRUB_STEP granularity the nudge buttons ever offered, and named
//     Moments already provide the "return to an exact point" use case
//     nudging was originally partly for. Removing them also reclaims
//     real horizontal width for the slider itself on iPad landscape.
//   - The permanent "Moment 1 / Moment 2 / ..." Chip row is GONE (§25).
//     Coach-created Moments are now small tappable dot landmarks living
//     in the same thin row as the SNAP label, directly on the timeline
//     — the timeline itself is the primary Moment-navigation affordance
//     now, not a separate row of buttons below it.
//   - Snap is no longer a big rectangular Button (§21) — it's the same
//     diamond landmark from B.3/B.3a, now REAL (tappable, not decorative)
//     with a large effective touch target and an always-visible "SNAP"
//     text label, living in the thin landmark row rather than overlapping
//     the slider's own draggable area (see the layout note below on why
//     that placement makes tap-vs-drag conflict a non-issue by
//     construction, not by careful pixel math).
//   - Formation/End remain real Buttons (§20/§22 — "a compact text
//     label/button... is acceptable"), just shrunk to minimal
//     text-button chrome instead of full outlined/contained rectangles,
//     and no longer rendered with a duplicate label anywhere else.
//   - The permanent "Post-Snap · 13%" phase/percent readout is REMOVED
//     entirely (§27) — the timeline's own thumb position relative to
//     the Formation label / SNAP landmark / End label already conveys
//     "roughly where in the lifecycle" at a glance; this was judged
//     sufficient without a redundant text row (§27 explicitly permits
//     removal "if the lifecycle timeline already communicates the
//     information sufficiently").
//
// LAYOUT NOTE — why tap-vs-drag conflict is a non-issue: the SNAP
// landmark and Moment dots live in a SEPARATE row directly below the
// Slider (not absolutely overlapping its own draggable thumb/track
// area), stacked inside the SAME flex:1 middle column as the Slider so
// both rows share identical width/left-offset by construction (no
// fragile spacer-width math against the Play/Pause/Mark Moment/
// Formation/End buttons on either side). A coach's drag gesture on the
// Slider itself can never land on a landmark's own hit-area, and vice
// versa — physically disjoint regions, not layered with z-index/
// pointer-events tricks.
//
// TOUCH TARGETS (§16/§30): every interactive element below keeps an
// effective hit area of roughly 40-44px even where the VISIBLE glyph is
// much smaller (the Snap diamond is ~8px drawn, but its tappable Box is
// 40x28px; Moment dots are ~7px drawn with a similar invisible hit
// area) — "reduce visual chrome, not usability."
//
// COACH-FACING TERMINOLOGY (§3 of B.2.1): saved timeline stops are
// called MOMENTS in every string a coach sees. Internal/technical
// naming (`keyframe`, `kind: 'keyframe'`, `position`) is deliberately
// UNCHANGED — a presentation-only vocabulary change, not a data-model
// rename.
//
// PTP_PREVIEW_DURATION_MS is a presentation-only UI animation speed for
// the Play/Pause preview — it is NOT football timing data, is never
// persisted anywhere. Exported (not module-local) since the hook using
// it is instantiated by the caller instead of here.
export const PTP_PREVIEW_DURATION_MS = 5000;

export default function PlaybookTimelineControls({
  fullScrub, moments, selectedKeyframe, renamingKeyframeId, renameDraft,
  playing, onPlay, onPause,
  // §19/§20 — true while the coach is actively drawing/redrawing a
  // path/Motion or placing a Zone center on the field. The WHOLE control
  // surface stays visible (never hidden — "field always present,
  // timeline always accessible") but becomes non-interactive, so a
  // stray tap on the timeline can never conflict with an in-progress
  // field authoring gesture, and Play can never start mid-draw.
  disabled,
  onScrub, onMarkThisPoint,
  onStartRename, onRenameDraftChange, onRenameCommit, onRenameCancel, onDelete,
}) {
  // Every MANUAL interaction pauses playback first (§7 of B.2.1, still
  // true here) — "don't fight the coach's finger." Safe/idempotent to
  // call onPause() even when playback isn't running. Jumping to
  // Formation/Snap/End or a Moment is really just "scrub directly to
  // this fullScrub value" — reuses `onScrub` rather than a separate
  // callback per anchor.
  const manualScrub = (v) => { onPause(); onScrub(v); };
  const manualMarkMoment = () => { onPause(); onMarkThisPoint(); };

  const isRenaming = renamingKeyframeId === selectedKeyframe?.id;
  const selectedIsMoment = Boolean(selectedKeyframe && selectedKeyframe.kind === 'keyframe');

  return (
    // Phase B.2.4 — field-first UX: no bordered/margined Card of its
    // own. The caller (DualMarkerField) composes this as the BOTTOM HUD
    // tray, floating over the field — this component only ever renders
    // its own content, letting the parent own the surrounding
    // translucent background.
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {/* MAIN ROW — Play/Pause, Mark Moment, Formation label, the
          slider (+ its own thin landmark row directly beneath it), End
          label. Wraps gracefully on narrow widths (§47) rather than
          forcing one row at the cost of tiny touch targets. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Button
          variant="contained" disabled={disabled} onClick={playing ? onPause : onPlay}
          aria-label={playing ? 'Pause' : 'Play'} title={playing ? 'Pause' : 'Play'}
          sx={{ textTransform: 'none', fontWeight: 800, minWidth: 44, minHeight: 44, width: 44, height: 44, borderRadius: '50%', p: 0, fontSize: '1.1rem', flexShrink: 0 }}
        >
          {playing ? '⏸' : '▶'}
        </Button>
        <Button
          disabled={disabled} onClick={manualMarkMoment}
          sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40, px: 1.25, fontSize: '0.75rem', flexShrink: 0 }}
        >
          + Moment
        </Button>
        <Button
          variant="text"
          disabled={disabled} onClick={() => manualScrub(FORMATION_FULL_SCRUB)}
          sx={{
            textTransform: 'none', fontWeight: 800, fontSize: '0.65rem', letterSpacing: '0.03em',
            minHeight: 40, px: 1, flexShrink: 0, color: isAtFormation(fullScrub) ? '#2ea84b' : '#6a8aa8',
          }}
        >
          FORMATION
        </Button>

        {/* Slider + its own thin landmark row — both direct children of
            this ONE flex:1 column, so they always share identical
            width/left-offset (see this file's own header note on why
            that's what makes tap-vs-drag conflict a non-issue). */}
        <Box sx={{ flex: 1, minWidth: 100, display: 'flex', flexDirection: 'column' }}>
          <Slider
            value={fullScrub} min={0} max={1} step={SCRUB_STEP} disabled={disabled}
            onChange={(e, v) => manualScrub(v)}
            sx={{
              py: 0.5,
              // Phase B.3h.2 (§Task A) — ROOT CAUSE of "thumb doesn't
              // visibly move during automatic Play, then jumps on
              // Pause": MUI's own Slider.js styles BOTH .MuiSlider-thumb
              // AND .MuiSlider-track with a default CSS
              // `transition: theme.transitions.create(['left', ...],
              // { duration: theme.transitions.duration.shortest })`
              // (150ms) — confirmed by reading MUI's actual installed
              // source (node_modules/@mui/material/Slider/Slider.js).
              // MUI disables this itself ONLY while its OWN internal
              // `.dragging` class is applied (an active pointer drag,
              // which is exactly why manual scrubbing was already
              // smooth) — there is no equivalent for a purely
              // PROGRAMMATIC `value` prop stream, which is exactly what
              // automatic playback is. During Play, `value` changes
              // ~60 times/sec; each change retriggers a fresh 150ms
              // transition toward the new target BEFORE the previous one
              // ever gets close to finishing, so the visible position
              // stays almost exactly where it started (the transition
              // curve is constantly restarted from a near-identical
              // point) — until updates stop (Pause), at which point the
              // final queued transition is finally allowed to complete,
              // producing the reported "sudden jump." Explicitly
              // overriding `transition: 'none'` on both elements makes
              // the visible thumb/track position track the controlled
              // `value` prop INSTANTLY regardless of whether the change
              // came from a drag or from Play — no new clock, no
              // throttling, no quantization; `fullScrub` itself was
              // always correct (proven in B.3h.1) and reaching this
              // component correctly every render (proven by the
              // Moment-row/dot-color part of this SAME component already
              // updating live) — only the CSS was silently eating the
              // motion.
              '& .MuiSlider-thumb': { width: 20, height: 20, transition: 'none' },
              '& .MuiSlider-track': { height: 4, transition: 'none' },
              '& .MuiSlider-rail': {
                height: 4, opacity: 1,
                // Pre-Snap portion (left of Snap) reads slightly darker/
                // cooler than Post-Snap — subtle phase shading, no
                // segmented-control framing.
                background: `linear-gradient(to right, #1c2e42 0%, #1c2e42 ${SNAP_FULL_SCRUB * 100}%, #274a63 ${SNAP_FULL_SCRUB * 100}%, #274a63 100%)`,
              },
            }}
          />
          <Box sx={{ position: 'relative', height: 20 }}>
            {/* SNAP landmark (§21) — the REAL navigation control now (no
                separate rectangular Snap button anywhere). Visible
                diamond ~8px; tappable Box is a generous ~40x20 hit
                area (§16). Always labeled. */}
            <Box
              role="button" tabIndex={disabled ? -1 : 0} aria-label="Jump to Snap"
              onClick={() => !disabled && manualScrub(SNAP_FULL_SCRUB)}
              sx={{
                position: 'absolute', top: 0, left: `${SNAP_FULL_SCRUB * 100}%`, transform: 'translateX(-50%)',
                width: 40, height: 20, display: 'flex', flexDirection: 'column', alignItems: 'center',
                cursor: disabled ? 'default' : 'pointer', pointerEvents: disabled ? 'none' : 'auto',
              }}
            >
              <Box sx={{ width: 8, height: 8, backgroundColor: '#f5a623', border: '1px solid rgba(0,0,0,0.4)', transform: 'rotate(45deg)', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#f5a623', lineHeight: 1.3, letterSpacing: '0.03em' }}>SNAP</Typography>
            </Box>
            {/* Coach-created Moments (§24/§25) — small tappable dots,
                own landmark row, no permanent text label (would clutter
                with more than one or two Moments — the currently-
                SELECTED Moment's name shows in the contextual row
                below instead). Never causes the slider itself to snap —
                this is a completely separate absolutely-positioned
                element, not a Slider `marks` prop. */}
            {moments.map(m => (
              <Box
                key={m.id}
                role="button" tabIndex={disabled ? -1 : 0} aria-label={`Jump to ${m.label}`}
                onClick={() => !disabled && manualScrub(m.fullScrub)}
                sx={{
                  position: 'absolute', top: 0, left: `${m.fullScrub * 100}%`, transform: 'translateX(-50%)',
                  width: 32, height: 20, display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
                  cursor: disabled ? 'default' : 'pointer', pointerEvents: disabled ? 'none' : 'auto',
                }}
              >
                <Box sx={{
                  width: 7, height: 7, borderRadius: '50%', mt: '0.5px',
                  backgroundColor: selectedKeyframe?.id === m.id ? '#2ea84b' : '#9eb8d0',
                  border: '1px solid rgba(0,0,0,0.35)',
                }} />
              </Box>
            ))}
          </Box>
        </Box>

        <Button
          variant="text"
          disabled={disabled} onClick={() => manualScrub(END_FULL_SCRUB)}
          sx={{
            textTransform: 'none', fontWeight: 800, fontSize: '0.65rem', letterSpacing: '0.03em',
            minHeight: 40, px: 1, flexShrink: 0, color: isAtEnd(fullScrub) ? '#2ea84b' : '#6a8aa8',
          }}
        >
          END
        </Button>
      </Box>

      {/* CONTEXTUAL ROW (§26 of B.3b originally) — Phase B.3h.2 (Task B):
          this row is now ALWAYS rendered/ALWAYS reserves its own height,
          so the Bottom HUD's total outer height never changes as
          playback crosses in and out of a Moment — previously this row
          was conditionally mounted/unmounted entirely (`{selectedIsMoment
          && (...)}`), which visibly twitched the whole HUD/field area
          taller and shorter every time playback entered or left a
          Moment. `minHeight: 40` matches the row's own Button/Typography
          content height (all already 40px), so a Moment being active
          doesn't change the row's height either — content only changes
          WHAT'S shown, never how much space it occupies. Visibility
          changes; geometry does not. Formation/Snap/End never populate
          this row's content (protected, unchanged) — it simply renders
          empty for them, at the same reserved height as everything
          else.
          Phase B.3h.4 (Task 7, small alignment polish) — left-aligned
          (was `justifyContent: 'center'`) so it reads as continuous with
          the main row's own left-to-right content instead of floating
          detached in the middle of the tray. Height/behavior unchanged. */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start', minHeight: 40 }}>
        {selectedIsMoment && (
          isRenaming ? (
            <>
              <TextField
                size="small" autoFocus value={renameDraft} disabled={disabled}
                onChange={e => onRenameDraftChange(e.target.value)}
                sx={{ width: 160 }}
              />
              <Button disabled={disabled} onClick={onRenameCommit} sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}>Save</Button>
              <Button disabled={disabled} onClick={onRenameCancel} sx={{ textTransform: 'none', color: '#6a8aa8', minHeight: 40 }}>Cancel</Button>
            </>
          ) : (
            <>
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#e8f0f8' }}>{selectedKeyframe.label}</Typography>
              <Button disabled={disabled} onClick={() => onStartRename(selectedKeyframe.id, selectedKeyframe.label)} sx={{ textTransform: 'none', minHeight: 40 }}>Rename</Button>
              <Button disabled={disabled} color="error" onClick={() => onDelete(selectedKeyframe.id)} sx={{ textTransform: 'none', minHeight: 40 }}>Delete</Button>
            </>
          )
        )}
      </Box>
    </Box>
  );
}
